import React from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { numberWithComma, formatDateTime } from 'helpers/format'
import { Col, Row, Space } from 'antd'
import { LogsData } from 'Store/stock/types'
import { Data as ProductData } from 'Store/product/types'
import PdfCompanyInfo from 'Pages/Purchase/components/PdfCompanyInfo'

type Props = {
  detail?: LogsData
  product?: ProductData
  productOptionId: number
}

const ManageStockPDF: React.FunctionComponent<Props> = props => {
  const { detail, product, productOptionId } = props

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const renderInfoItem = (title: any, value: any, justify: 'start' | 'end' = 'start') => {
    return (
      <Row gutter={[8, 8]} justify={justify}>
        <Col>
          <Space wrap={true}>
            <InfoTitle>
              {title} : {value || '-'}
            </InfoTitle>
          </Space>
        </Col>
      </Row>
    )
  }

  const renderProductName = (item?: ProductData) => {
    const optionName = item && item?.product_options.find(item => item.id === productOptionId)
    if (!optionName) return <RowTile>{item?.title}</RowTile>
    const isSameTitle = item?.title == optionName?.title
    return (
      <>
        <RowTile>{item?.title}</RowTile>
        {!isSameTitle && <ProductDesc>{optionName?.title}</ProductDesc>}​
      </>
    )
  }

  const renderTable = () => {
    return (
      <Table>
        <thead>
          <th className="left">เลขที่ GRNID</th>
          <th className="right">ราคาสินค้า(ทุน)</th>
          <th className="right">จำนวนปรับออก</th>
          <th className="right">ราคาต้นทุนรวม</th>
        </thead>
        <tbody>
          {detail?.logs &&
            detail?.logs.map((item, index) => {
              return (
                <tr key={index}>
                  <td className="left">{item.grn_no}</td>
                  <td className="right">{renderInfo(numberWithComma(item.cost || 0, true))}</td>
                  <td className="right">{renderInfo(numberWithComma(item.quantity || 0))}</td>
                  <td className="right">
                    {renderInfo(numberWithComma((item.quantity || 0) * (item.cost || 0), true))}
                  </td>
                </tr>
              )
            })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5}>
              <Row>
                <Col span={12}>{renderInfoItem('หมายเหตุ', detail?.reason)}</Col>
                <Col span={12}>
                  {renderInfoItem(
                    'มูลค่าต้นทุนสินค้ารวม',
                    <TotalPrice>{numberWithComma(detail?.total_cost || 0, true)}</TotalPrice>,
                    'end',
                  )}
                </Col>
              </Row>
            </td>
          </tr>
        </tfoot>
      </Table>
    )
  }

  return (
    <BoxWrapper>
      <Row justify="start">
        <Col>
          <PdfCompanyInfo merchant={product?.merchant} />
        </Col>
      </Row>
      <br />
      <Row justify="center">
        <Col>
          <Title>รายงานการปรับสินค้าออกจากคลัง</Title>
          <SmallTitle> (กรณีสินค้าชำรุด / สูญหาย / ย้ายคลัง)</SmallTitle>
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span={12}>
          <Row>
            <Col span={24}>{renderInfoItem('ชื่อสินค้า', renderProductName(product))}</Col>
            <Col span={24}>{renderInfoItem('รหัสสินค้า', product?.code)}</Col>
            <Col span={24}>{renderInfoItem('ยี่ห้อ', product?.manufacturer?.name)}</Col>
            <Col span={24}>{renderInfoItem('หน่วย', product?.uom?.name)}</Col>
          </Row>
        </Col>
        <Col span={12}>
          <Row>
            <Col span={24}>{renderInfoItem('ผู้ดำเนินการ', detail?.username, 'end')}</Col>
            <Col span={24}>{renderInfoItem('วันที่', formatDateTime(detail?.created_at || ''), 'end')}</Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col span={24}>{renderTable()}</Col>
      </Row>
      <Footer>
        <Row gutter={[32, 8]} justify="start">
          <Col>
            <Space direction="horizontal" align="end">
              <InfoValue>ลงชื่อยืนยันผู้ปรับสินค้าออก</InfoValue>
              <br />
              <Underline></Underline>
              <Underline>วันที่ &nbsp; &nbsp; &nbsp;/&nbsp;&nbsp; &nbsp;/&nbsp; &nbsp;&nbsp;</Underline>
            </Space>
          </Col>
        </Row>
      </Footer>
    </BoxWrapper>
  )
}

const Title = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.h4};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  text-align: center;
`

const SmallTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.h5};
  text-align: center;
  margin-bottom: 40px;
`

const BoxWrapper = styled.div`
  width: 0;
  height: 0;
  overflow: hidden;
  background-color: #fff;

  @media print {
    width: 100%;
    height: 100%;

    @page {
      width: 100%;
      height: 100vh;
      margin: 1cm;
    }

    table {
      page-break-after: auto;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    td {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    thead {
      display: table-header-group;
    }
    tfoot {
      display: contents;
    }
  }
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const Footer = styled.div`
  position: fixed;
  bottom: 0;
  height: 0;
  overflow: hidden;
  display: none;
  width: 90vw;

  @media print {
    padding: 1cm;
    height: auto;
    display: block;
  }
`

const RowTile = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  padding-left: 5px;
`

const InfoTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  white-space: pre-line;
  text-align: left;
`

const InfoValue = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const Underline = styled.div`
  width: 150px;
  border-bottom: 1px solid;
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const TotalPrice = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const Table = styled.table`
  width: 100%;
  margin: 20px 0;
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  th,
  td {
    border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.black};
    padding: 10px 5px;
    vertical-align: top;
    text-align: center;
  }
  th {
    font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.regular};
    vertical-align: middle;
    background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
    font-weight: 100;
  }

  .right {
    text-align: right;
  }
  .left {
    text-align: left;
  }
`

export default ManageStockPDF
